import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'hooks/useToast';
import ApiErrorResponse from 'services/API/axios/ApiErrorResponse';
import UserAPI from 'services/API/UserAPI';

type UserFromInviteCode = {
  email: string;
  first_name: string;
  last_name: string;
  job_title: string;
  company_name: string;
};

const renderInviteErrorToast = (
  title: string,
  message: string,
  code: string,
  renderToast: any,
) => {
  renderToast({
    toastTitle: code,
    title,
    label: message,
    status: 'error',
    duration: null,
  });
};

export const useUserFromInviteCode = (invite_code: string | null) => {
  const { renderToast } = useToast();
  const navigate = useNavigate();

  const userInfoFromInviteCode = useQuery({
    queryKey: ['fetchUserInfoFromInviteCode', invite_code],
    queryFn: async (): Promise<UserFromInviteCode> => {
      if (!invite_code) {
        return {
          email: '',
          first_name: '',
          last_name: '',
          job_title: '',
          company_name: '',
        };
      }
      const response = await UserAPI.verifyUserFromInviteHash(invite_code);

      return response?.data;
    },
    onError: (error: AxiosError) => {
      navigate('/login');
      if ((error?.response?.data as ApiErrorResponse)?.code === 'invite-code-expired') {
        renderInviteErrorToast(
          'Invite has expired',
          'Check your email for the newest invite',
          'invite-code-expired',
          renderToast,
        );
      } else if (
        (error?.response?.data as ApiErrorResponse)?.code === 'user-already-signed-up'
      ) {
        renderInviteErrorToast(
          'User already exists',
          'Sign in or reset your password',
          'user-already-signed-up',
          renderToast,
        );
      } else if ((error?.response?.data as ApiErrorResponse)?.code === 'not-found') {
        renderInviteErrorToast('Error', 'Invalid invite code', 'not-found', renderToast);
      } else {
        renderInviteErrorToast(
          'Error',
          'Invalid invite code',
          'invalid_invite_code',
          renderToast,
        );
      }
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  return { userInfoFromInviteCode };
};
